import { Dialog } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { Paperclip } from 'lucide-react';
import Button from 'shared/components/Button';
import FormField from 'shared/components/form/FormField';
import FormikInput from 'shared/components/form/FormikInput';
import { useThemeContext } from 'shared/context/themeContext';
import { dark as darkColors, secondary } from 'shared/theme/colorTokens';
import * as Yup from 'yup';
import { EventFileModel } from '../../models/Evento';
import { FileMutationInput } from '../../hooks/useEventoDetail';
import { PencilButton } from 'shared/components/IconButtons';

interface FormValues {
  name: string;
  observacion: string;
  file: File | null;
}

interface Props {
  open: boolean;
  title: string;
  file?: EventFileModel | null;
  onlyView?: boolean;
  onEdit?: () => void;
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: FileMutationInput) => void;
}

const FileUploadFormModal = ({
  open,
  title,
  onlyView,
  file,
  onEdit,
  isSaving,
  onClose,
  onSave
}: Props) => {
  const { theme } = useThemeContext();
  const isDark = theme === 'dark';

  const initialValues: FormValues = {
    name: file?.name ?? '',
    observacion: file?.observacion ?? '',
    file: null
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('El nombre es requerido'),
    observacion: Yup.string(),
    file: Yup.mixed().test(
      'file-required',
      'El archivo es requerido',
      (value) => !!value || !!file
    )
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: isDark ? darkColors.surface : secondary[100],
          borderRadius: '15px'
        }
      }}
    >
      <div className="flex flex-col gap-4 p-6">
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={(values) =>
            !onlyView
              ? onSave({
                  name: values.name,
                  observacion: values.observacion || undefined,
                  file: values.file ?? undefined,
                  existingUrl: file?.url ?? undefined
                })
              : onEdit?.()
          }
        >
          {({ values, errors, touched, setFieldValue, dirty }) => (
            <Form className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-ink dark:text-ink-dark">
                  {title}
                </h2>
                {onlyView && <PencilButton type="submit" size={16} />}
              </div>
              <FormikInput name="name" label="Nombre" disabled={onlyView} />
              <FormikInput
                name="observacion"
                label="Observación (opcional)"
                disabled={onlyView}
              />
              <FormField label="Archivo" error={touched.file && errors.file}>
                <label
                  htmlFor="file-upload-input"
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-line bg-whiteLight px-3 py-2 text-sm text-inkMuted transition hover:border-secondary-500 hover:text-secondary-600 dark:border-white/10 dark:bg-darkLight dark:text-inkMuted-dark"
                >
                  <Paperclip size={15} />
                  {values.file?.name ?? file?.name ?? 'Seleccionar archivo...'}
                </label>
                <input
                  id="file-upload-input"
                  type="file"
                  disabled={onlyView}
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) =>
                    setFieldValue('file', e.currentTarget.files?.[0] ?? null)
                  }
                />
              </FormField>
              {!onlyView && (
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={onClose}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" size="sm" disabled={isSaving || !dirty}>
                    {isSaving ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Dialog>
  );
};

export default FileUploadFormModal;
